import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useCursor } from '../context/CursorContext';
import { AnimatedSection } from '../components/AnimatedSection';

const API = process.env.REACT_APP_BACKEND_URL;
const RECIPIENT = 'varun@themoonmedia.com';

const JOBS = [
  {
    id: 'video-editor',
    closed: true,
    title: 'Video Editor',
    department: 'Production',
    type: 'Full-time / Contract',
    location: 'Remote',
    comp: 'Competitive — fiat or crypto-paid option available',
    tagline: 'Cut the videos that move markets.',
    summary:
      "We need a sharp, mid-to-senior editor who lives in Premiere, DaVinci, After Effects and Final Cut Pro — and uses AI tools to ship faster. You'll edit long-form YouTube content and Shorts for our roster of crypto creators.",
    responsibilities: [
      'Edit 2–3 polished videos per week across long-form YouTube and Shorts/Reels',
      'Layer in motion graphics, transitions, B-roll, and on-screen callouts',
      'Sync edits to creator voice and channel-specific style guides',
      'Use AI tools (transcription, auto-cut, voice clean-up, etc.) to compress turnaround',
      'Collaborate with the Video Producer on pacing, hooks, and retention edits',
    ],
    requirements: [
      '3+ years editing YouTube content (crypto / finance / tech a strong plus)',
      'Fluency in Premiere Pro, DaVinci Resolve, After Effects, Final Cut Pro',
      'Comfort with AI-assisted editing workflows',
      'Strong sense of pacing, story, and YouTube retention mechanics',
      'Reliable, deadline-driven, owns the output end-to-end',
    ],
    customFields: [
      { name: 'software_stack', label: 'Which editing software do you use daily?', type: 'checkbox-group', options: ['Premiere Pro', 'DaVinci Resolve', 'After Effects', 'Final Cut Pro', 'CapCut', 'Other'] },
      { name: 'ai_tools', label: 'Which AI tools have you actually used in production?', type: 'textarea', placeholder: 'e.g. Descript, ElevenLabs, Runway, Captions.ai, Opus Clip…' },
      { name: 'weekly_output', label: 'How many long-form videos can you ship per week?', type: 'text', placeholder: 'Honest answer, not aspirational' },
      { name: 'reel', label: 'Link to your best edit (Drive / YouTube / Vimeo)', type: 'url', required: true },
    ],
  },
  {
    id: 'video-producer',
    title: 'Video Producer',
    department: 'Production',
    type: 'Full-time',
    location: 'Remote-first, occasional travel',
    comp: 'Senior-level — fiat or crypto-paid option available',
    tagline: 'Run the show. Shape what gets made.',
    summary:
      "You'll own the production pipeline end-to-end — from concept and script through directing, editor management, and final delivery. You know what plays on YouTube right now and can spot a hook from a mile away. Crypto/Web3 fluency is mandatory.",
    responsibilities: [
      'Manage the full production lifecycle for multiple creators simultaneously',
      "Direct editors based on each creator's needs and current YouTube trends",
      'Concept and script video ideas optimized for retention and reach',
      "Analyze what works, kill what doesn't, iterate fast",
      'Coordinate with creators, editors, thumbnail designers, and the wider team',
    ],
    requirements: [
      '2–5+ years producing video content (YouTube-native experience required)',
      'Deep knowledge of crypto / Web3 — non-negotiable',
      'Proven ability to manage editors and creative talent',
      'Strong instinct for trends, hooks, and what makes content perform',
      'Studio production experience (lighting, audio, multi-cam) — a plus',
    ],
    customFields: [
      { name: 'crypto_experience', label: 'How long have you been in crypto / Web3?', type: 'select', options: ['< 1 year', '1–2 years', '2–4 years', '4+ years'] },
      { name: 'channels_worked', label: 'Which channels or creators have you produced for?', type: 'textarea', required: true, placeholder: 'Channel names + your specific role' },
      { name: 'best_video', label: "Link to the best video you've produced (and why it worked)", type: 'textarea', required: true },
      { name: 'team_size', label: "Largest team of editors / collaborators you've directly managed?", type: 'text' },
      { name: 'studio_setup', label: 'Studio production experience? (lighting, audio, multi-cam)', type: 'select', options: ['None', 'Some', 'Comfortable', 'Built one from scratch'] },
    ],
  },
  {
    id: 'creator-partnerships-operations-associate',
    closed: true,
    title: 'Creator Partnerships & Operations Associate',
    department: 'Partnerships & Operations',
    type: 'Full-time, on-site',
    location: 'Dubai, UAE',
    comp: 'AED 8,000 base + up to AED 2,000 performance — total up to AED 10,000/month',
    tagline: 'Be the operational engine of the agency.',
    summary: [
      "The Moon Media is a creator agency built around crypto's most-watched YouTube channels. We manage an ever-growing roster of creators, run The Moon Show live, and broker the sponsorship partnerships that sit behind them — working directly with global tier 1 exchanges. We're a small, fast team. There's no layer between you and the work, and no layer between you and the people making decisions.",
      "You'll be the operational engine of the agency: the person creators message first, and the person brands hear back from fastest. You'll own the day-to-day so the partnerships side can scale. Reports directly to the Agency Lead.",
    ],
    responsibilitiesTitle: "What you'll own",
    responsibilities: [
      { title: 'Creator communications', text: 'Daily point of contact for our creator roster. Chasing scripts, upload dates and sponsor mentions. Keeping creators unblocked without needing to be chased yourself.' },
      { title: 'Deliverables management', text: 'Track every sponsor obligation per creator (videos, CTAs, links, posts) against contract terms. Flag shortfalls before the sponsor does. Keep our Trello boards as the single source of truth.' },
      { title: 'The Moon Show live', text: 'Monitor livestreams, coordinate guests and run-of-show basics, confirm sponsor placements go out live, log KPIs after each show.' },
      { title: 'Business development support', text: "Build and qualify outreach lists of exchanges and Web3 brands. Send first-touch emails, book calls, prep one-pagers and proposals, keep the pipeline current. You won't be closing alone on day one. You will be by month six." },
      { title: 'Reporting and follow-through', text: 'Weekly deliverable reports, campaign performance summaries, and invoice and collection follow-ups with partners.' },
    ],
    requirementsTitle: 'Who you are',
    requirements: [
      '1–3 years in influencer marketing, agency account management, partnerships or business development',
      "Crypto or Web3 exposure strongly preferred — if you don't have it, you're genuinely curious and already following the space",
      'Dubai-based with a valid UAE residence visa (own, spouse or freelance). We are not sponsoring a visa for this role',
      'Ruthlessly organised. You live comfortably in Trello, Google Sheets, Telegram, Discord and email all day, and you close loops without reminders',
      'Clear written English. Most of this job is writing messages people actually reply to',
      "Comfortable across time zones — our creators and partners sit in the US, UK and Europe, and livestream hours don't run 9-to-5",
    ],
    extraSections: [
      {
        title: 'Nice to have',
        items: ['Familiarity with YouTube analytics, affiliate and UTM tracking, or basic commercial contract terms'],
      },
      {
        title: 'Compensation — up to AED 10,000 per month',
        items: [
          'AED 8,000 base, inclusive of housing and transport allowances',
          'Up to AED 2,000 monthly performance component, paid against clear published targets: deliverable accuracy, qualified BD meetings booked, and collections cleared',
        ],
      },
    ],
    customFields: [
      { name: 'uae_visa', label: 'Do you hold a valid UAE residence visa? (We are not sponsoring for this role)', type: 'select', required: true, options: ['Yes — own visa', 'Yes — spouse visa', 'Yes — freelance visa', 'No'] },
      { name: 'relevant_experience', label: 'Years in influencer marketing, agency account management, partnerships or BD', type: 'select', required: true, options: ['< 1 year', '1–3 years', '3–5 years', '5+ years'] },
      { name: 'crypto_exposure', label: 'Your crypto / Web3 exposure', type: 'textarea', required: true, placeholder: 'Roles, projects — or simply what you follow and why' },
      { name: 'tools_daily', label: 'Tools you use daily', type: 'checkbox-group', options: ['Trello', 'Google Sheets', 'Telegram', 'Discord', 'Notion', 'HubSpot / CRM'] },
      { name: 'first_touch_email', label: "Write the first-touch email you'd send to an exchange's partnerships lead", type: 'textarea', required: true, placeholder: '3–5 sentences. This is the writing test.' },
      { name: 'timezone_flex', label: 'Comfortable working across US / UK / Europe hours, including livestream evenings?', type: 'select', required: true, options: ['Yes', 'Mostly', 'No'] },
    ],
  },
  {
    id: 'content-creator',
    title: 'Content Creator',
    department: 'Talent',
    type: 'Full-time / Long-term partnership',
    location: 'Remote, global',
    comp: 'Revenue share + brand deals + production support — crypto-paid option available',
    tagline: 'If you trade crypto and create on YouTube — we want you.',
    summary:
      "We're looking for on-camera talent who are crypto-native traders with an existing YouTube channel and audience. We'll plug you into TheMoonMedia network — production support, premium brand deals, collab opportunities with the wider roster, and incubation infrastructure to scale your channel.",
    responsibilities: [
      'Create on-camera content for your YouTube channel (primary) and secondary platforms',
      'Share market views, trades, analysis, and your authentic voice',
      'Collaborate with other creators in the network',
      'Work with our production team to scale output and quality',
      "Maintain your audience's trust and stay creator-first",
    ],
    requirements: [
      'Active YouTube channel with an existing audience (mandatory)',
      'Crypto / Web3 native — actively trading is required',
      'Comfortable on camera',
      'Solo creator energy — entrepreneurial, opinionated, consistent',
      'Open to long-term partnership with TheMoonMedia',
    ],
    customFields: [
      { name: 'youtube_channel', label: 'Your YouTube channel URL', type: 'url', required: true },
      { name: 'subscriber_count', label: 'Current subscriber count', type: 'text', required: true, placeholder: 'Honest number' },
      { name: 'other_platforms', label: 'Other platforms + handles (X, TikTok, Instagram, etc.)', type: 'textarea' },
      { name: 'trading_background', label: "How long have you been trading crypto? What's your style?", type: 'textarea', required: true, placeholder: 'Spot, futures, on-chain, narratives, etc.' },
      { name: 'best_video_link', label: "Link to the video you're most proud of", type: 'url', required: true },
      { name: 'monthly_output', label: 'Videos published per month right now', type: 'text' },
    ],
  },
];

const OPEN_JOBS = JOBS.filter((j) => !j.closed);

const STANDARD_FIELDS = [
  { name: 'full_name', label: 'Full name', type: 'text', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'phone', label: 'Phone (optional)', type: 'tel' },
  { name: 'location', label: 'Location & timezone', type: 'text', required: true, placeholder: 'e.g. Dubai, GMT+4' },
  { name: 'linkedin', label: 'LinkedIn / X profile', type: 'url' },
  { name: 'portfolio', label: 'Portfolio / website (if any)', type: 'url' },
  { name: 'compensation', label: 'Compensation expectation (USD, monthly)', type: 'text', required: true, placeholder: 'We also pay in crypto if preferred' },
  { name: 'crypto_pay', label: 'Open to being paid in crypto?', type: 'select', required: true, options: ['Yes — preferred', 'Yes — open to it', 'No — fiat only'] },
  { name: 'start_date', label: 'Earliest start date', type: 'text', required: true, placeholder: 'e.g. Immediately, 2 weeks, June 1' },
];

const UNIVERSAL_FIELDS = [
  { name: 'favorite_creator', label: 'Favorite content creator (and why)', type: 'textarea', required: true, placeholder: 'The one who shaped how you think about content' },
  { name: 'dream_video', label: 'Describe your dream video / project', type: 'textarea', required: true, placeholder: 'If budget and time were unlimited — what would you make?' },
  { name: 'why_themoonmedia', label: 'Why TheMoonMedia, specifically?', type: 'textarea', required: true },
];

const inputCls =
  'w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-green-500 focus:outline-none transition-colors duration-300 placeholder:text-white/30';

export default function CareersPage() {
  const { setLabel } = useCursor();
  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => {
    document.body.style.overflow = activeJob ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [activeJob]);

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Now Hiring</p>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight">
            BUILD THE FUTURE OF<br />
            <span className="text-green-500">CRYPTO CONTENT.</span>
          </h1>
          <p className="text-white/40 text-sm max-w-xl mx-auto">
            TheMoonMedia is the creative agency for crypto's most influential voices. We incubate creators, run premium brand deals, and ship content that actually moves the needle. If that sounds like you — keep scrolling.
          </p>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-white text-xl font-bold uppercase tracking-wider">Open Roles</h2>
            <span className="text-green-500 text-xs font-semibold" data-testid="careers-role-count">{OPEN_JOBS.length} positions</span>
          </div>
          <div className="border-t border-white/5">
            {OPEN_JOBS.map((job, i) => (
              <JobRow key={job.id} job={job} index={i} onOpen={() => setActiveJob(job)} setLabel={setLabel} />
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection delay={200} className="mt-16 text-center">
          <p className="text-white/40 text-sm">
            Don't see your role? We're always open to exceptional people. Email{' '}
            <a href={`mailto:${RECIPIENT}`} className="text-green-500 hover:text-green-400 transition-colors">{RECIPIENT}</a>{' '}
            with what you'd bring to the table.
          </p>
        </AnimatedSection>
      </div>

      {activeJob && <JobModal job={activeJob} onClose={() => setActiveJob(null)} setLabel={setLabel} />}
    </div>
  );
}

function JobRow({ job, index, onOpen, setLabel }) {
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setLabel('Open')}
      onMouseLeave={() => setLabel('')}
      data-testid={`job-row-${job.id}`}
      className="group w-full flex items-center justify-between py-7 px-2 border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-300 text-left"
    >
      <div className="flex items-center gap-6 flex-1 min-w-0">
        <span className="text-white/20 text-sm font-mono">0{index + 1}</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-white text-xl md:text-2xl font-black group-hover:text-green-500 transition-colors duration-300">
            {job.title}
          </h3>
          <p className="text-white/40 text-sm mt-1">{job.tagline}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-white/30 uppercase tracking-wider">
            <span>{job.department}</span>
            <span>•</span>
            <span>{job.location}</span>
            <span>•</span>
            <span>{job.type}</span>
          </div>
        </div>
      </div>
      <span className="text-green-500 text-xs font-bold uppercase tracking-wider shrink-0 ml-4 group-hover:translate-x-1 transition-transform duration-300">
        Apply →
      </span>
    </button>
  );
}

function JobModal({ job, onClose, setLabel }) {
  const [view, setView] = useState('details');

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-testid="job-modal-backdrop"
      className="fixed inset-0 z-[9000] bg-black/90 backdrop-blur-sm overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="min-h-screen flex items-start justify-center p-4 md:p-12"
      >
        <div className="w-full max-w-2xl bg-black border border-white/10 relative" data-testid="job-modal">
          <button
            onClick={onClose}
            onMouseEnter={() => setLabel('Close')}
            onMouseLeave={() => setLabel('')}
            data-testid="job-modal-close"
            className="absolute top-4 right-4 text-white/40 hover:text-white text-xl w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>

          <div className="p-8 border-b border-white/10">
            <p className="text-green-500 text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">{job.department}</p>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">{job.title}</h2>
            <p className="text-white/50 text-sm mb-4">{job.tagline}</p>
            <div className="flex flex-wrap gap-2">
              <Tag>{job.type}</Tag>
              <Tag>{job.location}</Tag>
              <Tag accent>{job.comp}</Tag>
            </div>
          </div>

          {view === 'details' && (
            <DetailsView job={job} onApply={() => setView('apply')} setLabel={setLabel} />
          )}
          {view === 'apply' && (
            <ApplyView
              job={job}
              onBack={() => setView('details')}
              onSuccess={() => { onClose(); toast.success("Application submitted. We'll be in touch."); }}
              setLabel={setLabel}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Tag({ children, accent }) {
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${accent ? 'border-green-500/40 text-green-500' : 'border-white/10 text-white/50'}`}>
      {children}
    </span>
  );
}

function DetailsView({ job, onApply, setLabel }) {
  const paragraphs = [].concat(job.summary);
  const sections = [
    { title: job.responsibilitiesTitle || "What you'll do", items: job.responsibilities },
    { title: job.requirementsTitle || 'What we need from you', items: job.requirements },
    ...(job.extraSections || []),
  ];

  return (
    <div className="p-8">
      <div className="mb-8 space-y-4">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-white/70 text-sm leading-relaxed">{p}</p>
        ))}
      </div>

      {sections.map((s) => (
        <Section key={s.title} title={s.title}>
          <BulletList items={s.items} />
        </Section>
      ))}

      <button
        onClick={onApply}
        onMouseEnter={() => setLabel('Apply')}
        onMouseLeave={() => setLabel('')}
        data-testid="open-apply-form-btn"
        className="w-full mt-4 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300"
      >
        Apply for this role →
      </button>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((r, i) => (
        <li key={i} className="text-white/60 text-sm flex gap-3">
          <span className="text-green-500 shrink-0">▸</span>
          <span>
            {typeof r === 'string' ? r : (
              <><span className="text-white font-semibold">{r.title}</span> — {r.text}</>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="text-white/50 text-[10px] uppercase tracking-[0.3em] font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function ApplyView({ job, onBack, onSuccess, setLabel }) {
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({});

  const setField = (name, value) => setValues((v) => ({ ...v, [name]: value }));

  const allFields = [...STANDARD_FIELDS, ...job.customFields, ...UNIVERSAL_FIELDS];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/careers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_id: job.id,
          role_title: job.title,
          fields: values,
        }),
      });
      if (!res.ok) throw new Error();
      onSuccess();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-8" data-testid="careers-apply-form">
      <button
        type="button"
        onClick={onBack}
        className="text-white/40 hover:text-white text-xs uppercase tracking-wider mb-6 transition-colors"
        data-testid="back-to-details-btn"
      >
        ← Back to role details
      </button>

      <FieldGroup title="About you">
        {STANDARD_FIELDS.map((f) => (
          <Field key={f.name} field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
        ))}
      </FieldGroup>

      <FieldGroup title="Role-specific">
        {job.customFields.map((f) => (
          <Field key={f.name} field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
        ))}
      </FieldGroup>

      <FieldGroup title="The personality test">
        {UNIVERSAL_FIELDS.map((f) => (
          <Field key={f.name} field={f} value={values[f.name]} onChange={(v) => setField(f.name, v)} />
        ))}
      </FieldGroup>

      <button
        type="submit"
        disabled={submitting}
        onMouseEnter={() => setLabel('Send')}
        onMouseLeave={() => setLabel('')}
        data-testid="submit-careers-application-btn"
        className="w-full py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 disabled:opacity-50"
      >
        {submitting ? 'Sending…' : 'Submit Application →'}
      </button>

      <p className="text-white/20 text-[10px] mt-4 text-center">
        By submitting, you agree to TheMoonMedia processing your application details. Submissions go directly to {RECIPIENT}.
      </p>
    </form>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-green-500 text-[10px] uppercase tracking-[0.3em] font-semibold mb-4 pb-2 border-b border-white/5">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  const id = `field-${field.name}`;
  return (
    <div>
      <label htmlFor={id} className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">
        {field.label}{field.required && <span className="text-green-500 ml-1">*</span>}
      </label>

      {field.type === 'textarea' && (
        <textarea
          id={id}
          name={field.name}
          required={field.required}
          placeholder={field.placeholder || ''}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} h-24 resize-none`}
          data-testid={`field-${field.name}`}
        />
      )}

      {field.type === 'select' && (
        <select
          id={id}
          name={field.name}
          required={field.required}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} cursor-pointer appearance-none`}
          data-testid={`field-${field.name}`}
        >
          <option value="" className="bg-black">Select…</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt} className="bg-black">{opt}</option>
          ))}
        </select>
      )}

      {field.type === 'checkbox-group' && (
        <div className="grid grid-cols-2 gap-2 mt-1">
          {field.options.map((opt) => {
            const selected = (value || []).includes(opt);
            return (
              <label key={opt} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => {
                    const next = selected
                      ? (value || []).filter((v) => v !== opt)
                      : [...(value || []), opt];
                    onChange(next);
                  }}
                  className="accent-green-500"
                />
                {opt}
              </label>
            );
          })}
        </div>
      )}

      {!['textarea', 'select', 'checkbox-group'].includes(field.type) && (
        <input
          id={id}
          name={field.name}
          type={field.type}
          required={field.required}
          placeholder={field.placeholder || ''}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          data-testid={`field-${field.name}`}
        />
      )}
    </div>
  );
}
